import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useFormik, Form, FormikProvider, FieldArray } from 'formik';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
// material
import {
  Stack,
  TextField,
  Box,
  Fab,
  Button,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  FormHelperText,
  FormGroup,
  FormControlLabel,
  FormLabel,
  Checkbox,
  IconButton
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Icon } from '@iconify/react';
import plusFill from '@iconify/icons-eva/plus-fill';
import trashFill from '@iconify/icons-eva/trash-2-fill';
import checkmarkCircleFill from '@iconify/icons-eva/checkmark-circle-2-fill';
// contexts
import { useAuth } from '../../../contexts/Auth';
import { SettingsContext } from '../../../contexts/Settings';
import { EventContext } from '../../../contexts/Event';
// services
import { updateEvent } from '../../../services/Event';
import { updateTicketMany } from '../../../services/Ticket';
import { updateInventoryMany } from '../../../services/Inventory';

dayjs.extend(utc);
dayjs.extend(timezone);

EventFormCalendar.propTypes = {
  event: PropTypes.object,
  handleNext: PropTypes.func.isRequired,
  handleBack: PropTypes.func.isRequired,
  edit: PropTypes.bool
};

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// TODO: Precisa receber o event ou pode pegar do context?
export default function EventFormCalendar({ event, handleNext, handleBack, edit }) {
  const { authUser } = useAuth();
  const { authSettings } = useContext(SettingsContext);
  const { setEvent } = useContext(EventContext);

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // TODO: Melhorar o TMP
  const [calendarValues, setCalendarValues] = useState({
    calendar: {
      recurrence: {
        frequency: '',
        weekdays: []
      },
      schedule: [
        {
          start: null,
          tmpStart: null,
          end: null,
          tmpEnd: null
        }
      ]
    }
  });

  useEffect(() => {
    if (event?.calendar?.recurrence?.frequency) {
      const schedule = event.calendar.schedule.map((schedule) => ({
        start: new Date(schedule.start).toLocaleString('en', {
          timeZone: 'Europe/Lisbon'
        }),
        tmpStart: new Date(schedule.start).toLocaleString('en', {
          timeZone: 'Europe/Lisbon'
        }),
        end: new Date(schedule.end).toLocaleString('en', {
          timeZone: 'Europe/Lisbon'
        }),
        tmpEnd: new Date(schedule.end).toLocaleString('en', {
          timeZone: 'Europe/Lisbon'
        })
      }));

      setCalendarValues({
        calendar: {
          ...event.calendar,
          schedule
        }
      });
    }
  }, [event]);

  // TODO: Validar data com Yup: formato e se é maior que hoje, mes tem que ser de 1 a 12, etc...
  const EventCalendarSchema = Yup.object().shape({
    calendar: Yup.object({
      recurrence: Yup.object({
        frequency: Yup.string().required('Frequency is required'),
        weekdays: Yup.array()
      }),
      schedule: Yup.array().of(
        Yup.object().shape({
          start: Yup.date().nullable().required('Start date is required'),
          end: Yup.date().nullable().required('End date is required')
        })
      )
    })
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: calendarValues,
    validationSchema: EventCalendarSchema,
    onSubmit: async (values) => {
      setIsSubmitted(false);
      setIsLoading(true);

      try {
        const idToken = await authUser.getIdToken();
        const headers = {
          Authorization: `Bearer ${idToken}`,
          'Account-Key': authSettings.server.path
        };

        values.calendar.schedule = values.calendar.schedule.map((schedule) => ({
          start: dayjs(schedule.start).tz('Europe/Lisbon', true).toISOString(),
          end: dayjs(schedule.end).tz('Europe/Lisbon', true).toISOString()
        }));

        const payload = {
          calendar: {
            recurrence: {
              frequency: values.calendar.recurrence.frequency,
              weekdays: values.calendar.recurrence.weekdays
            },
            schedule: values.calendar.schedule
          }
        };

        await updateEvent({ headers, payload, id: event._id });

        // TODO: Atualizar usando kafka (provisório)
        const payloadServices = {
          event: {
            _id: event._id,
            calendar: {
              recurrence: {
                frequency: values.calendar.recurrence.frequency,
                weekdays: values.calendar.recurrence.weekdays
              },
              schedule: values.calendar.schedule
            }
          }
        };
        await updateTicketMany({ headers, payload: payloadServices });
        await updateInventoryMany({ headers, payload: payloadServices });
        setEvent((event) => ({ ...event, ...payload }));

        setIsLoading(false);

        if (!edit) {
          handleNext();
        } else {
          setIsSubmitted(true);
        }
      } catch (err) {
        setIsLoading(false);
        console.error(err);
        alert('An error occurred! Please, try again.');
      }
    }
  });

  const { values, errors, touched, handleSubmit, getFieldProps, setFieldValue } = formik;

  return (
    <Box>
      <FormikProvider value={formik}>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Frequency*</InputLabel>
              <Select
                label="Frequency*"
                {...getFieldProps('calendar.recurrence.frequency')}
                error={Boolean(
                  touched.calendar?.recurrence?.frequency && errors.calendar?.recurrence?.frequency
                )}
                onChange={(e) => {
                  setFieldValue(`calendar.recurrence.frequency`, e.target.value);
                  setFieldValue(`calendar.recurrence.weekdays`, []);
                  setFieldValue(`calendar.schedule`, [values.calendar.schedule[0]]);
                }}
              >
                <MenuItem value="daily">Daily</MenuItem>
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="no-repeat">Only Once</MenuItem>
              </Select>
              <FormHelperText
                error={Boolean(
                  touched.calendar?.recurrence?.frequency && errors.calendar?.recurrence?.frequency
                )}
              >
                {touched.calendar?.recurrence?.frequency && errors.calendar?.recurrence?.frequency}
              </FormHelperText>
            </FormControl>
            {values.calendar.recurrence.frequency === 'weekly' && (
              <FormControl sx={{ pb: 2 }} component="fieldset">
                <FormLabel component="legend">Weekdays</FormLabel>
                <FormGroup aria-label="position" row>
                  {WEEKDAYS.map((weekday) => (
                    <FormControlLabel
                      key={weekday}
                      control={<Checkbox name="calendar.recurrence.weekdays" value={weekday} />}
                      label={weekday}
                      {...getFieldProps('calendar.recurrence.weekdays')}
                      checked={values.calendar.recurrence.weekdays.includes(weekday)}
                      onChange={(selectedWeekday) => {
                        /*
                         ** IMPORTANT: It is important that the days of the week are in order
                         ** So, use WEEKDAYS to filter
                         */
                        let selectedWeekdays = [];
                        if (values.calendar.recurrence.weekdays.includes(weekday)) {
                          selectedWeekdays = WEEKDAYS.filter(
                            (currentWeekday) =>
                              values.calendar.recurrence.weekdays.includes(currentWeekday) &&
                              currentWeekday !== selectedWeekday.target.value
                          );
                        } else {
                          selectedWeekdays = WEEKDAYS.filter(
                            (currentWeekday) =>
                              values.calendar.recurrence.weekdays.includes(currentWeekday) ||
                              currentWeekday === selectedWeekday.target.value
                          );
                        }

                        setFieldValue(`calendar.recurrence.weekdays`, selectedWeekdays);
                      }}
                    />
                  ))}
                </FormGroup>
              </FormControl>
            )}
            <FieldArray
              name="calendar.schedule"
              render={(fieldArrayHelpers) => (
                <>
                  {values.calendar.schedule.map((schedule, i) => (
                    <LocalizationProvider key={i} dateAdapter={AdapterDayjs}>
                      <Stack direction="row" spacing={2} alignItems="flex-start">
                        <DatePicker
                          label="Start Date*"
                          value={schedule.tmpStart || null}
                          name="start"
                          showToolbar={false}
                          onChange={(start) => {
                            setFieldValue(`calendar.schedule.${i}.tmpStart`, start);
                            // PS: Aqui não é para coloca timezone Lisbon
                            // Pois pega o date/timezone atual e depois converte
                            const startDate = new Date(start);
                            startDate.setHours(0, 0, 0, 0);
                            setFieldValue(
                              `calendar.schedule.${i}.start`,
                              dayjs(startDate).tz('Europe/Lisbon', true)
                            );
                          }}
                          renderInput={(params) => (
                            <TextField
                              fullWidth
                              {...getFieldProps(`calendar.schedule.${i}.start`)}
                              helperText={
                                (touched.calendar?.schedule &&
                                  touched.calendar?.schedule[i]?.start &&
                                  errors.calendar?.schedule &&
                                  errors.calendar?.schedule[i]?.start) ||
                                params?.inputProps?.placeholder
                              }
                              {...params}
                            />
                          )}
                        />
                        <DatePicker
                          label="End Date*"
                          // value={schedule.end || null}
                          value={schedule.tmpEnd || null}
                          // value={dayjs(schedule.tmpEnd).tz('Europe/Lisbon', true)}
                          name="end"
                          showToolbar={false}
                          onChange={(end) => {
                            setFieldValue(`calendar.schedule.${i}.tmpEnd`, end);
                            // PS: Aqui não é para coloca timezone Lisbon
                            // Pois pega o date/timezone atual e depois converte
                            const endDate = new Date(end);
                            endDate.setHours(23, 59, 59, 999);
                            setFieldValue(
                              `calendar.schedule.${i}.end`,
                              dayjs(endDate).tz('Europe/Lisbon', true)
                            );
                          }}
                          renderInput={(params) => (
                            <TextField
                              fullWidth
                              {...getFieldProps(`calendar.schedule.${i}.end`)}
                              helperText={
                                (touched.calendar?.schedule &&
                                  touched.calendar?.schedule[i]?.end &&
                                  errors.calendar?.schedule &&
                                  errors.calendar?.schedule[i]?.end) ||
                                params?.inputProps?.placeholder
                              }
                              {...params}
                            />
                          )}
                        />
                        {i > 0 && (
                          <IconButton
                            sx={{ top: '8px' }}
                            color="error"
                            aria-label="delete"
                            onClick={() => fieldArrayHelpers.remove(i)}
                          >
                            <Icon icon={trashFill} />
                          </IconButton>
                        )}
                      </Stack>
                    </LocalizationProvider>
                  ))}

                  {values.calendar.recurrence.frequency === 'no-repeat' && (
                    <Box sx={{ textAlign: 'center', mt: -3 }}>
                      <Fab
                        size="medium"
                        color="primary"
                        aria-label="add"
                        onClick={() =>
                          fieldArrayHelpers.push({
                            start: null,
                            tmpStart: null,
                            end: null,
                            tmpEnd: null
                          })
                        }
                      >
                        <Icon icon={plusFill} width="24" />
                      </Fab>
                    </Box>
                  )}
                </>
              )}
            />

            <Box>
              {!edit && (
                <>
                  <Button size="large" onClick={handleBack} sx={{ mr: 1 }}>
                    Back
                  </Button>
                  <Button size="large" variant="outlined" onClick={handleNext} sx={{ mr: 1 }}>
                    Next
                  </Button>
                </>
              )}
              <LoadingButton
                size="large"
                type="submit"
                variant="contained"
                loading={isLoading}
                endIcon={isSubmitted && <Icon icon={checkmarkCircleFill} />}
              >
                Save
              </LoadingButton>
            </Box>
          </Stack>
        </Form>
      </FormikProvider>
    </Box>
  );
}
